import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { restServices } from '../../../rest-services';
import { UtilsService } from './utils/utils.service';
import { StorageService } from './utils/storage.service';
import { TipoProfilo, UserInfoModel } from '../models/models';
import { environment } from 'src/environments/environment';
import { LOGGED_USER, LOGGED_USER_PROFILE } from '../constants/app-constants';

@Injectable({ providedIn: 'root' })
export class AccountService {

    private userIdentity: UserInfoModel | null = null;
    private authenticationState = new BehaviorSubject<UserInfoModel | null>(null);
    private accountCache$!: Observable<UserInfoModel>;
    private nomeCognome$ = new BehaviorSubject<string>('');
    private userData$ = new BehaviorSubject<UserInfoModel | null>(null);
    private lastVerifiedAt = 0;
    private readonly AUTH_TTL_MS = 30_000;


    constructor(private router: Router,
        private http: HttpClient,
        private utils: UtilsService,
        private storage: StorageService) {
        if (this.storage.hasItemLocal(LOGGED_USER)) {
            this.saveAccountData(JSON.parse(this.storage.getItemLocal(LOGGED_USER)), false);
        }
    }

    authenticate(identity: UserInfoModel): void {
        this.userIdentity = identity;
        this.authenticationState.next(this.userIdentity);
    }

    isAuthCheckFresh(): boolean {
        return !!this.userIdentity && (Date.now() - this.lastVerifiedAt) < this.AUTH_TTL_MS;
    }

    clearAuth(): void {
        this.userIdentity = null;
        this.authenticationState.next(null);
        this.accountCache$ = null!;
        this.lastVerifiedAt = 0;
    }

    logout(): void {
        this.storage.clearAll();
        this.clearAuth();
        this.router.navigate(['/auth/login']);
    }

    hasProfilo(profilo: TipoProfilo): boolean {
        return !!this.userIdentity?.profili?.find(p => p.profilo === profilo);
    }

    hasAnyProfilo(profili: TipoProfilo[]): boolean {
        if (!this.userIdentity) {
            return false;
        }
        return this.userIdentity.profili?.length ? this.userIdentity.profili.some(p => profili.includes(p.profilo)) : false;
    }

    identity(force?: boolean): Observable<UserInfoModel> {

        if (!this.accountCache$ || force || !this.isAuthenticated()) {
            this.accountCache$ = this.fetch().pipe(
                tap((account: UserInfoModel) => {
                    this.lastVerifiedAt = Date.now();
                    this.authenticate(account);
                    if (account) {
                        this.saveAccountData(account);
                    }
                }),
                shareReplay({ bufferSize: 1, refCount: true })
            );
        }

        return this.accountCache$!;
    }


    saveAccountData(account: UserInfoModel, saveOnStorage = true) {
        if (saveOnStorage) {
            this.storage.setItemLocal(LOGGED_USER, JSON.stringify(account));
            this.storage.setItemLocal(LOGGED_USER_PROFILE, JSON.stringify({nome: account?.nome, cognome: account?.cognome}));
        }
        this.userData$.next(account);
        this.nomeCognome$.next(this.utils.smartConcat([account.nome, account.cognome]));
    }

    // Recupero info utente tramite servizio BE user-info (authorizations, uid, displayName..):
    private fetch(): Observable<UserInfoModel> {
        return this.http.get<UserInfoModel>(`/api/${restServices.userInfo}`);
    }

    isAuthenticated(): boolean {
        // potrei aver cancellato la cache del browser mentre ero loggato	per cui controllo che anche LOGGED_USER
        const loggedUser: UserInfoModel = this.getLoggedUserData();
        return loggedUser && !!this.userIdentity;
    }

    getAuthenticationState(): Observable<UserInfoModel | null> {
        return this.authenticationState.asObservable();
    }

    getLoggedUserData(): UserInfoModel {
        return JSON.parse(this.storage.getItemLocal(LOGGED_USER));
    }

    sendInvitation(email: string): Observable<any> {
        return this.http.post(`${environment.baseApiUrl}${restServices.sendInvite}`, { email: email });
    }

    activationUser(email: string): Observable<any> {
        return this.http.post(`${environment.baseApiUrl}${restServices.activationUser}`, { email: email });
    }

    get nomeCognomeUser$(): Observable<string> {
        return this.nomeCognome$.asObservable();
    }

    get nomeCognomeUser(): string {
        return this.nomeCognome$.getValue();
    }

    get userData(): UserInfoModel | null {
        return this.userData$.getValue();
    }

}
